import { memo } from 'react';

// Country name and flag mapping for ISO codes
const countryInfo: Record<string, { name: string; flag: string }> = {
    FR: { name: 'France', flag: '🇫🇷' },
    US: { name: 'États-Unis', flag: '🇺🇸' },
    GB: { name: 'Royaume-Uni', flag: '🇬🇧' },
    DE: { name: 'Allemagne', flag: '🇩🇪' },
    ES: { name: 'Espagne', flag: '🇪🇸' },
    IT: { name: 'Italie', flag: '🇮🇹' },
    CA: { name: 'Canada', flag: '🇨🇦' },
    AU: { name: 'Australie', flag: '🇦🇺' },
    JP: { name: 'Japon', flag: '🇯🇵' },
    CN: { name: 'Chine', flag: '🇨🇳' },
    IN: { name: 'Inde', flag: '🇮🇳' },
    BR: { name: 'Brésil', flag: '🇧🇷' },
    MX: { name: 'Mexique', flag: '🇲🇽' },
    RU: { name: 'Russie', flag: '🇷🇺' },
    KR: { name: 'Corée du Sud', flag: '🇰🇷' },
    NL: { name: 'Pays-Bas', flag: '🇳🇱' },
    BE: { name: 'Belgique', flag: '🇧🇪' },
    CH: { name: 'Suisse', flag: '🇨🇭' },
    PT: { name: 'Portugal', flag: '🇵🇹' },
    PL: { name: 'Pologne', flag: '🇵🇱' },
    SE: { name: 'Suède', flag: '🇸🇪' },
    NO: { name: 'Norvège', flag: '🇳🇴' },
    DK: { name: 'Danemark', flag: '🇩🇰' },
    FI: { name: 'Finlande', flag: '🇫🇮' },
    AT: { name: 'Autriche', flag: '🇦🇹' },
    IE: { name: 'Irlande', flag: '🇮🇪' },
    NZ: { name: 'Nouvelle-Zélande', flag: '🇳🇿' },
    SG: { name: 'Singapour', flag: '🇸🇬' },
    HK: { name: 'Hong Kong', flag: '🇭🇰' },
    TW: { name: 'Taïwan', flag: '🇹🇼' },
    TH: { name: 'Thaïlande', flag: '🇹🇭' },
    VN: { name: 'Vietnam', flag: '🇻🇳' },
    ID: { name: 'Indonésie', flag: '🇮🇩' },
    MY: { name: 'Malaisie', flag: '🇲🇾' },
    PH: { name: 'Philippines', flag: '🇵🇭' },
    ZA: { name: 'Afrique du Sud', flag: '🇿🇦' },
    AR: { name: 'Argentine', flag: '🇦🇷' },
    CL: { name: 'Chili', flag: '🇨🇱' },
    CO: { name: 'Colombie', flag: '🇨🇴' },
    PE: { name: 'Pérou', flag: '🇵🇪' },
    EG: { name: 'Égypte', flag: '🇪🇬' },
    MA: { name: 'Maroc', flag: '🇲🇦' },
    NG: { name: 'Nigeria', flag: '🇳🇬' },
    KE: { name: 'Kenya', flag: '🇰🇪' },
    AE: { name: 'Émirats Arabes Unis', flag: '🇦🇪' },
    SA: { name: 'Arabie Saoudite', flag: '🇸🇦' },
    IL: { name: 'Israël', flag: '🇮🇱' },
    TR: { name: 'Turquie', flag: '🇹🇷' },
    UA: { name: 'Ukraine', flag: '🇺🇦' },
    CZ: { name: 'Tchéquie', flag: '🇨🇿' },
    RO: { name: 'Roumanie', flag: '🇷🇴' },
    HU: { name: 'Hongrie', flag: '🇭🇺' },
    GR: { name: 'Grèce', flag: '🇬🇷' },
    Unknown: { name: 'Inconnu', flag: '🌍' },
    XX: { name: 'Inconnu', flag: '🌍' },
};

interface Props {
    countryData: Record<string, number>; // { FR: 10, US: 5, ... }
}

const WorldHeatmap = memo(({ countryData }: Props) => {
    // Sort countries by click count (descending)
    const sortedCountries = Object.entries(countryData)
        .filter(([_, count]) => count > 0)
        .sort((a, b) => b[1] - a[1]);

    const totalClicks = sortedCountries.reduce((sum, [_, count]) => sum + count, 0);

    if (sortedCountries.length === 0) {
        return (
            <div className="world-heatmap-empty">
                <p className="muted">Pas encore de données géographiques</p>
            </div>
        );
    }

    return (
        <div className="world-heatmap">
            <div className="heatmap-header">
                <span className="heatmap-title">🌍 Pays</span>
                <span className="heatmap-countries">{sortedCountries.length} pays</span>
            </div>
            <div className="country-list">
                {sortedCountries.map(([code, count]) => {
                    const info = countryInfo[code.toUpperCase()] || countryInfo['Unknown'];
                    const percent = Math.round((count / totalClicks) * 100);
                    return (
                        <div key={code} className="country-item">
                            <span className="country-flag">{info.flag}</span>
                            <span className="country-name">{info.name}</span>
                            <span className="country-count">{count}</span>
                            <span className="country-percent">{percent}%</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

WorldHeatmap.displayName = 'WorldHeatmap';

export default WorldHeatmap;
